/* eslint-disable react-refresh/only-export-components */
import { Link, Outlet, useLocation } from "react-router-dom";

import { AdminIcon } from "@/components/admin/icon";
import {
	canAccessTab,
	SubNav,
	type SubNavTab,
} from "@/components/admin/sub-nav";
import { usePermissions } from "@/hooks/use-permissions";

/**
 * Layout de section : barre d'onglets (sous-navigation) au-dessus des pages
 * « liste » d'un même domaine, et contrôle d'accès par URL — un onglet masqué
 * par les permissions n'est pas non plus atteignable en tapant son adresse.
 * Les pages de détail restent hors de ce layout.
 */
export function SectionTabsLayout({
	tabs,
	testId,
}: {
	tabs: SubNavTab[];
	testId?: string;
}) {
	const { pathname } = useLocation();
	const { hasAny, isSuperAdmin } = usePermissions();

	// Onglet correspondant à l'URL courante : préfixe le plus long d'abord,
	// pour que /commandes/recherche matche « Recherche » et non « Commandes ».
	const current = [...tabs]
		.sort((a, b) => b.to.length - a.to.length)
		.find((t) =>
			t.end
				? pathname === t.to
				: pathname === t.to || pathname.startsWith(`${t.to}/`),
		);

	if (current && !canAccessTab(current, hasAny, isSuperAdmin)) {
		const fallback = tabs.find((t) => canAccessTab(t, hasAny, isSuperAdmin));
		return <SectionAccessDenied fallback={fallback} />;
	}

	return (
		<>
			<SubNav tabs={tabs} testId={testId} />
			<Outlet />
		</>
	);
}

function SectionAccessDenied({ fallback }: { fallback?: SubNavTab }) {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: 12,
				padding: "72px 24px",
				textAlign: "center",
			}}
			data-testid="section-access-denied"
		>
			<span
				style={{
					display: "inline-flex",
					padding: 14,
					borderRadius: "50%",
					background: "var(--a-terra-soft)",
					color: "var(--a-terracotta)",
				}}
			>
				<AdminIcon n="shield" s={26} c="currentColor" />
			</span>
			<h2 style={{ fontSize: 17, fontWeight: 600, color: "var(--a-ink)" }}>
				Accès restreint
			</h2>
			<p style={{ fontSize: 13, color: "var(--a-ink-soft)", maxWidth: 380 }}>
				Votre compte ne dispose pas des permissions nécessaires pour consulter
				cette page. Contactez un administrateur si vous pensez qu'il s'agit
				d'une erreur.
			</p>
			{fallback && (
				<Link
					to={fallback.to}
					className="btn"
					style={{ marginTop: 6 }}
					data-testid="section-access-denied-back"
				>
					Aller à « {fallback.label} »
				</Link>
			)}
		</div>
	);
}

export const DASHBOARD_TABS: SubNavTab[] = [
	{ to: "/", label: "Vue d'ensemble", icn: "home", end: true },
	{
		to: "/reporting",
		label: "Reporting",
		icn: "chart",
		permissions: [
			"SYSTEM.AUDIT.READ",
			"FINANCE.READ",
			"LOGISTICS.READ",
			"SUPPORT.READ",
		],
	},
];

export const ORDERS_TABS: SubNavTab[] = [
	{
		to: "/commandes",
		label: "Commandes",
		icn: "cart",
		end: true,
		permissions: ["ORDERS.READ"],
	},
	{
		to: "/commandes/recherche",
		label: "Recherche",
		icn: "search",
		permissions: ["ORDERS.READ"],
	},
	{
		to: "/commandes/statistiques",
		label: "Statistiques",
		icn: "chart",
		permissions: ["ORDERS.READ"],
	},
	{
		to: "/retours",
		label: "Retours (RMA)",
		icn: "refresh",
		permissions: ["ORDERS.RETURNS.READ"],
	},
];

/**
 * Les contenus éditoriaux n'ont plus d'onglets ici : ils sont passés dans la
 * colonne de « Marketing & Contenus » (voir config/marketing-nav.ts), avec les
 * quinze autres écrans du domaine.
 */
export const SHORT_LINKS_TABS: SubNavTab[] = [
	{
		to: "/liens-courts",
		label: "Liens",
		icn: "zap",
		end: true,
		permissions: ["MARKETING.CMS.READ", "MARKETING.CMS.WRITE"],
	},
	{
		to: "/liens-courts/statistiques",
		label: "Statistiques",
		icn: "chart",
		permissions: ["MARKETING.CMS.READ", "MARKETING.CMS.WRITE"],
	},
];

export const CATALOG_TABS: SubNavTab[] = [
	{
		to: "/catalogue",
		label: "Produits",
		icn: "box",
		end: true,
		permissions: ["CATALOG.READ"],
	},
	{
		to: "/categories",
		label: "Catégories",
		icn: "layout",
		permissions: ["CATALOG.READ"],
	},
	{ to: "/marques", label: "Marques", icn: "tag" },
	{ to: "/attributs", label: "Attributs", icn: "filter" },
	{ to: "/catalogue/import-export", label: "Import / Export", icn: "download" },
	{ to: "/catalogue/medias", label: "Médias", icn: "eye" },
	{ to: "/avis-produits", label: "Avis produits", icn: "star" },
	{ to: "/catalogue/assistant-ia", label: "Assistant IA", icn: "chat" },
];

export const PAYMENTS_TABS: SubNavTab[] = [
	{
		to: "/paiements",
		label: "Paiements",
		icn: "card",
		end: true,
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
	{
		to: "/paiements/momo-live",
		label: "MoMo Live",
		icn: "trend",
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
	{
		to: "/paiements/remboursements",
		label: "Remboursements",
		icn: "refresh",
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
	{
		to: "/paiements/rapprochement",
		label: "Rapprochement",
		icn: "check",
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
	{
		to: "/paiements/litiges",
		label: "Litiges",
		icn: "alert",
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
	{
		to: "/paiements/prestataires",
		label: "Prestataires",
		icn: "factory",
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
	{
		to: "/paiements/cod",
		label: "COD",
		icn: "truck",
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
	{
		to: "/paiements/comptes-bancaires",
		label: "Comptes bancaires",
		icn: "card",
		permissions: ["PAYMENT.READ", "FINANCE.READ"],
	},
];

export const FINANCE_TABS: SubNavTab[] = [
	{
		to: "/finance",
		label: "Tableau de bord",
		icn: "bank",
		end: true,
		permissions: ["FINANCE.READ"],
	},
	{
		to: "/finance/factures",
		label: "Factures",
		icn: "card",
		permissions: ["FINANCE.READ"],
	},
	{
		to: "/finance/avoirs",
		label: "Avoirs",
		icn: "refresh",
		permissions: ["FINANCE.READ", "FINANCE.CREDIT_NOTE.ISSUE"],
	},
	{
		to: "/finance/journaux",
		label: "Journaux",
		icn: "layout",
		permissions: ["FINANCE.READ"],
	},
	{
		to: "/finance/plan-comptable",
		label: "Plan comptable",
		icn: "chart",
		permissions: ["FINANCE.READ"],
	},
	{
		to: "/finance/tva",
		label: "TVA",
		icn: "flag",
		permissions: ["FINANCE.READ"],
	},
	{
		to: "/finance/exports",
		label: "Exports",
		icn: "download",
		permissions: ["FINANCE.EXPORT", "FINANCE.EXPORT.ACCOUNTING"],
	},
	{
		to: "/finance/etats",
		label: "États & clôture",
		icn: "calendar",
		permissions: ["FINANCE.STATEMENTS", "FINANCE.READ"],
	},
	{
		to: "/finance/budgets",
		label: "Budgets",
		icn: "trend",
		permissions: ["FINANCE.BUDGET.EDIT", "FINANCE.READ"],
	},
	{
		to: "/finance/audit",
		label: "Audit",
		icn: "shield",
		permissions: ["FINANCE.AUDIT"],
	},
];

export const LOGISTICS_TABS: SubNavTab[] = [
	{
		to: "/logistique",
		label: "Logistique",
		icn: "truck",
		end: true,
		permissions: ["LOGISTICS.READ"],
	},
	{
		to: "/logistique/zones",
		label: "Zones de livraison",
		icn: "map",
		permissions: ["LOGISTICS.READ"],
	},
];

export const CUSTOMERS_TABS: SubNavTab[] = [
	{
		to: "/clients",
		label: "Clients",
		icn: "users",
		end: true,
		permissions: ["CUSTOMERS.READ"],
	},
	{
		to: "/clients/segments",
		label: "Segments",
		icn: "filter",
		permissions: ["CUSTOMERS.READ"],
	},
	{
		to: "/clients/fidelite",
		label: "Fidélité",
		icn: "star",
		permissions: ["CUSTOMERS.READ"],
	},
	{
		to: "/clients/parrainage",
		label: "Parrainage",
		icn: "user",
		permissions: ["CUSTOMERS.READ"],
	},
	{
		to: "/clients/rgpd",
		label: "RGPD",
		icn: "shield",
		permissions: ["CUSTOMERS.READ"],
	},
	{
		to: "/clients/statistiques",
		label: "Statistiques",
		icn: "chart",
		permissions: ["CUSTOMERS.READ"],
	},
];
